import React, { useContext, useState } from 'react';
import { Grid2 as Grid, Card, CardContent, CardHeader, Typography, Stack, Link, Chip, useTheme, alpha, Alert} from '@mui/material';
import { FilersDataContext } from '../../Filers';
import DocumentViewer from '../DocumentViewer/DocumentViewer';
import EmailIcon from '@mui/icons-material/Email';
import FilerAuditLetter from '../../../../types/filers/filerAuditLetter';
import FilerAuditResponse from '../../../../types/filers/filerAuditResponse';

const FilerAudit = (): JSX.Element => {

    const theme = useTheme();
    const filerDataContext = useContext(FilersDataContext);
    const filerAuditIssues = filerDataContext.currentFilerAuditIssues;

    const [isDocumentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [selectedLetter, setSelectedLetter] = useState<FilerAuditLetter>();
    const [selectedResponse, setSelectedResponse] = useState<FilerAuditResponse>();
    
    const openDocumentViewerByLetter = (letter: FilerAuditLetter) => {
        setDocumentViewerOpen(true);
        setSelectedLetter(letter);
      };
    
      const openDocumentViewerByResponse = (response: FilerAuditResponse) => {
        setDocumentViewerOpen(true);
        setSelectedResponse(response);
      };

      const closeDocumentViewer = () => {
        setDocumentViewerOpen(false);
        setSelectedLetter(undefined);  
        setSelectedResponse(undefined); 
      };

    return (
        <Grid container margin={2} spacing={4}>
          {
            filerAuditIssues.length === 0 &&
             <Grid size={12} marginBottom={1} marginTop={1}>
                  <Alert icon={false} severity="info"
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'center',
                        maxWidth: '95%',
                        '.MuiAlert-message': { 
                          padding: '4px',
                        } 
                      }}
                    >
                    No Audit Issues Found.
                  </Alert>
              </Grid>
          }
          {filerAuditIssues.map((issue) => (
            <Grid size={{xs:12, md:4, lg: 3.6, sm:6}} key={issue.id}>
              <Card 
                variant="outlined"
                sx={{ height: '100%' }}
                >
                <CardHeader
                    title={
                      <Stack direction="row" spacing={1}>
                        <Typography variant={'subtitle2'} fontWeight={550}>
                          ISSUE:
                        </Typography>
                        <Typography variant={'subtitle2'}>
                            {issue.dateDisplay} {issue.description}
                        </Typography>
                    </Stack>
                    }
                    sx={{backgroundColor: "#f5f5f5", padding: "8px"}}
                />
                <CardContent className="card-content">
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Typography variant="subtitle2" paddingTop={"3px"} fontWeight={550}>
                            Audit Letters:
                        </Typography>
                        {
                            issue.sentLetters.map((letter, index) => (
                                <Stack direction={"row"} spacing={0.1} key={index}>
                                  <EmailIcon fontSize="small" color="action" />
                                  <Link underline='none'>
                                    <Typography component={'i'} className="is-clickable" variant="body2" onClick={()=>openDocumentViewerByLetter(letter)}>
                                        {letter.dateSentDisplay} {letter.letterType}
                                    </Typography>
                                  </Link>
                                </Stack>
                            ))
                        }
                    </Stack>
                    <Stack direction="row" spacing={1} marginTop={0.5} useFlexGap flexWrap="wrap">
                        <Typography variant="subtitle2" paddingTop={"3px"} fontWeight={550}>
                            Responses:
                        </Typography>
                          {
                            issue.responses.length === 0 &&
                            <Chip label="no responses yet" size="small" color="warning" variant="outlined"/>
                          }
                          {
                            issue.responses.length >0 &&
                            issue.responses.map((response, index) => (
                              <React.Fragment key={index}>
                              {
                                response.responseFormat === "Letter/Email" && response.blobUrl &&
                                <Link underline="none">
                                  <Typography component={'i'} className="is-clickable" variant="body2" onClick={()=>openDocumentViewerByResponse(response)}>
                                      {response.responseFormat}
                                  </Typography>
                                </Link>
                              }
                              {
                                   response.responseFormat === "Letter/Email" && !response.blobUrl &&
                                   <Chip label={response.description} size="small" color="primary" variant="outlined"/>
                              }
                              {
                                response.responseFormat === "Report" &&
                                <Link href={`/reports/displayreport?id=${response.reportId}`} target="_blank">
                                  <Typography component={'i'} className="is-clickable" variant="body2">
                                      {response.linkDescription}
                                  </Typography>
                                </Link>
                              }
                              </React.Fragment>
                               
                            ))
                        }
                    </Stack>
                    <Stack direction="row" spacing={1} marginTop={0.5}>
                        <Typography variant="subtitle2" paddingTop={"3px"} fontWeight={550}>
                            Status:
                        </Typography>
                        <Typography
                          variant={'caption'}
                          fontWeight={700}
                          sx={{
                            bgcolor: issue.status === "Outstanding"? alpha(theme.palette.error.light, 0.1): alpha(theme.palette.success.light, 0.1),
                            color: issue.status === "Outstanding"? theme.palette.error.dark: theme.palette.success.dark,
                            paddingX: 1.5,
                            paddingY: 0.5,
                            borderRadius: 4,
                            display: 'inline',
                            }}
                          >
                          {issue.status}
                          </Typography>
                    </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
          <DocumentViewer isDocumentViewerOpen={isDocumentViewerOpen} closeDocumentViewer={closeDocumentViewer} letter={selectedLetter} response={selectedResponse}></DocumentViewer>
        </Grid>
    );
  };

export default FilerAudit;